import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { StoredCollectionNames } from '../../models/stored-collection-names';
import { ApiService } from './api/api.service';
import { AdminDocument } from '../../models/admin-document';
import { Article } from '../../models/article';
import { ExtractedContext, StoredContext } from '../../models/context';

interface ContextOptions {
  currentNavigationState?: { [k: string]: any };
  openSource: string;
  openSourceId: string;
  collectiveOrderId: number;
  document: AdminDocument | Article;
}

@Injectable({
  providedIn: 'root'
})
export class ContextService {

  // Time to live, in milliseconds.
  TTL = 24 * 60 * 60 * 1000; // one day

  constructor(
    private apiService: ApiService,
    private localStorageService: LocalStorageService
  ) { }

  async getSharedDocumentContext(shareId: string) {
    const document = await firstValueFrom(this.apiService.share.getSharedDocument(shareId)).catch((e)=> {
      console.log(`error when retrieving context from share: ${e}`);
    });
    return document;
  }

  async retrieveDigestContext(documentId: string, collectiveOrderId: number) {
    const s3DeduplicatedFileUrl = await firstValueFrom(
      this.apiService.territoryWatchSending.retrieveS3DeduplicatedDigestResultsFileUrlFromCollectiveOrderId(
        collectiveOrderId
      )
    ).catch((e) => {
      console.log(
        `error when retrieving s3DeduplicatedFileUrl from collective order: ${e}`
      );
    });
    if (!s3DeduplicatedFileUrl) {
      return null;
    }
    const digestResults = await this.apiService.territoryWatchSending.retrieveDigestResultsFromS3(
      s3DeduplicatedFileUrl
    );
    let document ;
    for (const entry of digestResults?.digests_by_content_type ?? []) {
      if (entry.search_results) {
        const results = Array.isArray(entry.search_results)
          ? entry.search_results
          : entry.search_results.data;

        const match = results.find((item) => item.id === documentId);
        if (match) document = match;
      }
    }
    return {
      digest_results_url: s3DeduplicatedFileUrl,
      digest: digestResults,
      document
    };
  }

  async retrieveContextFromCollectiveOrder(
    documentId: string,
    collectiveOrderId: number
  ): Promise<ExtractedContext | null | undefined> {
    try {
      const response: any = await firstValueFrom(
        this.apiService.territoryWatchSending.retrieveContextFromCollectiveOrder(
          collectiveOrderId
        )
      );
      for (const doc of response.docs) {
        if (doc.id === documentId) {
          return {
            snippet: doc.excerpt,
            title: doc.title,
          };
        }
      }
      return null;
    } catch (e) {
      console.log(`error when retrieving context from collective order: ${e}`);
      return null;
    }
  }

  async getContext(
    options: ContextOptions
  ): Promise<ExtractedContext | null | undefined> {
    const {
      currentNavigationState,
      openSource,
      openSourceId,
      collectiveOrderId,
      document,
    } = options;

    const defaultContext = this.getDefaultContext(
      currentNavigationState,
      this.findStoredContext(document.id),
      document
    );
    switch (openSource) {
      case 'share': {
        const shareContext = await this.getShareContext(openSourceId);
        if (!shareContext) return defaultContext;
        return {
          ...shareContext,
          title: shareContext.title || defaultContext.title,
          snippet: shareContext.snippet || defaultContext.snippet,
        };
      }

      case 'digest': {
        const digestContext = await this.getDigestContext(
          document.id,
          collectiveOrderId
        );
        if (!digestContext) return defaultContext;
        return {
          ...digestContext,
          title: digestContext.title || defaultContext.title,
          snippet: digestContext.snippet || defaultContext.snippet,
        };
      }

      default:
        return defaultContext;
    }
  }

  private async getShareContext(
    openSourceId: string
  ): Promise<ExtractedContext | null> {
    const sharedDocument = await this.getSharedDocumentContext(openSourceId);
    return {
      snippet: sharedDocument?.snippet,
      title: sharedDocument?.title,
    };
  }

  private async getDigestContext(
    documentId: string,
    collectiveOrderId: number
  ): Promise<ExtractedContext | null | undefined> {
    const digestContext = await this.retrieveDigestContext(
      documentId,
      collectiveOrderId
    );

    if (digestContext) {
      return {
        digest_results_url: digestContext.digest_results_url,
        is_newsletter: digestContext.digest?.is_newsletter,
        snippet: digestContext.document?.relevant_excerpt ?? digestContext.document?.excerpt?.[0],
        title: digestContext.document?.summary ?? digestContext.document?.title,
      };
    }

    return this.retrieveContextFromCollectiveOrder(
      documentId,
      collectiveOrderId
    );
  }

  private getDefaultContext(
    currentNavigationState: { [k: string]: any } | undefined,
    storedContext: any,
    document: AdminDocument | Article
  ): ExtractedContext {
    if (storedContext) {
      return {
        snippet: storedContext.snippet,
        title: storedContext.title,
      };
    }

    return {
      snippet: document.snippet ?? currentNavigationState?.['context'],
      title: document.title,
    };
  }

  private findStoredContext(documentId: string): any {
    const storedContexts = this.getStoredContexts() || { storedItems: [] };
    return storedContexts.find((item: any) => item?.id === documentId);
  }

  /**
   * Method to store a context inside localStorage with a TTL (time to live) list of storedContexts.
   * @param documentId document id
   * @param snippet context
   */
  setContextWithExpiry(documentId: string, extractedContext: ExtractedContext) {
    const itemToStore: StoredContext = {
      ...extractedContext,
      id: documentId,
      expiry: Date.now() + this.TTL,
      url: this.constructUrl(documentId),
      snippet: extractedContext.snippet,
    };
    this.localStorageService.storeItem(
      StoredCollectionNames.STORED_CONTEXTS,
      itemToStore
    );
  }

  constructUrl(documentId: string): string {
    const baseUrl = window.location.href;

    if (baseUrl.endsWith('article') || baseUrl.endsWith('admin-doc')) {
      return `${baseUrl}/${documentId}`;
    }

    return baseUrl;
  }

  setProjectUidWithExpiry(documentId: string, projectUid: string): void {
    if (!projectUid) {return; }
    const itemToStore = {
      id: documentId,
      projectUid: projectUid,
      expiry: Date.now() + this.TTL
    };
    this.localStorageService.storeItem(StoredCollectionNames.STORED_PROJECT_UIDS, itemToStore);
  }

  getStoredProjectUids(): any {
    const storedProjectUids = localStorage.getItem(StoredCollectionNames.STORED_PROJECT_UIDS);
    if(storedProjectUids) return JSON.parse(storedProjectUids)?.storedItems;
  }

  getStoredContexts(): any {
    const storedContexts = localStorage.getItem(StoredCollectionNames.STORED_CONTEXTS);
    if(storedContexts) return JSON.parse(storedContexts).storedItems;
  }
}

